/**
 * Slice VI-01 · CaptureCoachingBanner — bounded checkpoint prompts.
 *
 * Locked Product Owner direction embodied here:
 *  - Latency honest. This is NOT real-time conversational coaching.
 *    Prompts appear seconds after a segment finalizes transcription.
 *    UI copy says "heads-up" / "aviso", never "real-time".
 *  - At most one active prompt is shown at a time (§C default
 *    max_active_prompts_client_side = 1). Dismissable.
 *  - Structured variables render through the localization system.
 *    Prose is NEVER read from database rows.
 *  - Dismissal writes dismissed_at on the server and starts cooldown.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, X } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

// Localized prompt copy is rendered through the LangContext dictionary.
// Prompt kinds map to i18n keys — no baked-in text lives in this file.
const PROMPT_KIND_I18N = {
  move_slower: "vi01.coaching.move_slower",
  hold_steady: "vi01.coaching.hold_steady",
  improve_lighting: "vi01.coaching.improve_lighting",
  move_farther_back: "vi01.coaching.move_farther_back",
  show_opposite_wall: "vi01.coaching.show_opposite_wall",
  include_both_corners: "vi01.coaching.include_both_corners",
  capture_ceiling: "vi01.coaching.capture_ceiling",
  provide_scale_reference: "vi01.coaching.provide_scale_reference",
  revisit_obstructed_area: "vi01.coaching.revisit_obstructed_area",
  clarify_ambiguity: "vi01.coaching.clarify_ambiguity",
};

export default function CaptureCoachingBanner({ sessionId, pollingIntervalMs = 6000 }) {
  const { t } = useLang();
  const [prompts, setPrompts] = useState([]);
  const [dismissing, setDismissing] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/video-walks/${sessionId}/coaching-prompts`);
      setPrompts(r.data.prompts || []);
    } catch {
      setPrompts([]);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    refresh();
    const iv = setInterval(refresh, pollingIntervalMs);
    return () => clearInterval(iv);
  }, [sessionId, refresh, pollingIntervalMs]);

  const active = useMemo(() => {
    const now = new Date().toISOString();
    return prompts
      .filter((p) => !p.resolved_at && !p.dismissed_at)
      .filter((p) => !p.cooldown_expires_at || p.cooldown_expires_at > now || true)
      .filter((p) => PROMPT_KIND_I18N[p.prompt_kind])
      .sort((a, b) => {
        // Warn severity before info; then most recent.
        const sev = (a.severity === "warn" ? -1 : 0) - (b.severity === "warn" ? -1 : 0);
        if (sev !== 0) return sev;
        return (b.emitted_at || "").localeCompare(a.emitted_at || "");
      });
  }, [prompts]);

  // Client-side max active prompts = 1 (§C default).
  const primary = active[0];

  const dismiss = async (promptId) => {
    setDismissing((s) => ({ ...s, [promptId]: true }));
    try {
      await api.post(
        `/video-walks/${sessionId}/coaching-prompts/${promptId}/action`,
        { action: "dismiss" }
      );
      await refresh();
    } finally {
      setDismissing((s) => ({ ...s, [promptId]: false }));
    }
  };

  if (!primary) return null;

  const Icon = primary.severity === "warn" ? AlertTriangle : Info;
  const iconClass = primary.severity === "warn"
    ? "text-[var(--jc-yellow)]"
    : "text-[var(--jc-blue)]";
  const promptCopy = t(PROMPT_KIND_I18N[primary.prompt_kind] || "");

  return (
    <div
      className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-surface)] p-3 flex items-start gap-3"
      data-testid="vi01-coaching-banner"
      data-prompt-kind={primary.prompt_kind}
      data-severity={primary.severity}
    >
      <Icon size={16} className={`${iconClass} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-[var(--jc-text-soft)]">
          {t("vi01.coaching.heading")}
        </div>
        <div className="text-sm mt-0.5">{promptCopy}</div>
      </div>
      <button
        type="button"
        onClick={() => dismiss(primary.id)}
        disabled={!!dismissing[primary.id]}
        className="text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] disabled:opacity-50"
        data-testid={`vi01-coaching-dismiss-${primary.id}`}
        aria-label={t("vi01.coaching.dismiss_aria")}
      >
        <X size={14} />
      </button>
    </div>
  );
}
